// CSR-1844 — Render-time formatter for the global Activity Log page.
//
// Pure-function module (no React, no I/O) consumed by `ActivityLogs.tsx`:
// defensive details parsing, the internal-event hide predicate, actor
// attribution, and before→after bullet lines with the Event Logging Spec's
// formatting (long dates, 2-decimal money, "(empty)") — never raw JSON,
// never ISO timestamps, never "[object Object]".

import type { ActivityFieldChange } from "@shared/activity-changes";
import {
  EMPTY_DISPLAY,
  FIELD_LABELS,
  formatCurrencyValue,
} from "@/lib/policy-activity-formatter";
import { INSURED_FIELD_LABELS } from "@/lib/insured-field-labels";
import { formatLongDate } from "@/lib/formatters";
import { formatPolicySubstatusLabel, isPolicySubstatus } from "@shared/policy-substatus";

// Subset of ActivityLogWithUser the renderer needs — keeps the module testable
// without the full API type.
export interface ActivityLogLike {
  action: string;
  entityType: string | null;
  entityId?: string | null;
  entityName?: string | null;
  details: string | null;
  userName?: string | null;
  userEmail?: string | null;
  userId?: string | null;
}

// Money fields mirror the server's MONEY_POLICY_FIELDS (storage.ts, CSR-1604):
// premium is the headline Total, the rest its breakdown components.
const MONEY_ACTIVITY_FIELDS = new Set([
  "premium",
  "policyPremium",
  "brokerFee",
  "policyFees",
  "taxes",
]);

const DATE_ACTIVITY_FIELDS = new Set([
  "effectiveDate",
  "expirationDate",
  "boundDate",
  "cancellationDate",
]);

// Detail keys with dedicated rendering rules — excluded from the flat-map
// fallback so their raw shapes never render as "key: value" JSON noise.
const HANDLED_DETAIL_KEYS = new Set([
  "changed",
  "changes",
  "coverageKeys",
  "assetStatusChange",
  "note",
]);

// Known flat-map "updated" maintenance shapes that carry no before→after diff
// but ARE genuine user actions (COI template / notes upkeep — routes.ts:666,
// storage.ts upsertInsuredCoiNotes / upsertInsuredCoiTemplate). Rendered as a
// fixed human line so they read cleanly instead of "Cleared Coi Template: true"
// or (for the array-valued coiNotes) an empty cell.
const FLAT_ACTION_LINES: Record<string, string> = {
  clearedCoiTemplate: "COI template cleared",
  savedCoiTemplate: "COI template saved",
  coiNotes: "COI notes updated",
};

// Pure record-identifier keys — present on an "updated" row only to name the
// record, never as edited content. An "updated" policy/insured row whose ONLY
// content is an identifier (the spec's "policy updated with only a policy
// number") is a no-op audit row the hide predicate drops. Excluded from the
// hide-decision content count ONLY — buildActivityDetailLines still renders
// them for other shapes (e.g. a create row's "Name: …").
const IDENTIFIER_ONLY_KEYS = new Set([
  "policyNumber",
  "id",
  "insuredId",
  "externalId",
  "entityName",
  "dotNumber",
  "name",
]);

// Labels for flat-map detail keys not covered by the policy/insured label
// maps (moved from the page's retired KEY_LABELS).
const EXTRA_KEY_LABELS: Record<string, string> = {
  policyId: "Policy",
  documentType: "Document Type",
  fileName: "File Name",
  source: "Source",
  region: "Region",
};

// ---------------- hide predicate ----------------

// Spec hide-list: internal/engineering actions never shown on the page.
// Display-only — legacy DB rows are untouched. The only actions expected to
// survive are the user-facing verbs: created, updated, deleted, archived,
// unarchived, generated, uploaded.
//
// CSR-1844 — this explicit set is the documented, complete inventory of the
// internal literal actions written today (grepped across every direct
// createActivityLog call AND every wrapper — logCoiActivity, logActivity,
// logReplyActivity, logCoiRefreshActivity — that forwards an action, named or
// positional). The HIDDEN_ACTION_SUBSTRINGS below are the family-closers that
// catch any future sibling in the same lifecycle family, so a newly-added
// internal action can't leak onto the page (belt-and-suspenders, matching the
// existing orphan_debounce_restore + "orphan"/"debounce" precedent).
const HIDDEN_ACTIONS = new Set([
  // sync family (→ "sync" substring)
  "synced",
  "synced_coverages_from_nowcerts",
  "synced_nowcerts",
  "manual_sync",
  "sync_failed_for_coi",
  "sync_skipped_for_coi", // R-004 — coiPolicyFreshness.ts logCoiRefreshActivity
  // email lifecycle family (→ "email" substring)
  "emailed",
  "email_queued",
  "email_failed",
  // auto_* provisioning family (→ "auto_" substring)
  "auto_created",
  "auto_save_failed",
  "auto_approved", // R-005 — coiUnmappedFlow.ts logActivity/logReplyActivity (entityType coi_request)
  // legacy cleanup family (→ "orphan"/"debounce"/"soft_delete"/"csr_" substrings)
  "orphan_debounce_restore",
]);

// Family-closing rule catches so a future internal sibling (or a legacy DB row)
// is hidden without needing to enumerate it. Every internal lifecycle family is
// covered here: sync / email / auto_ provisioning / orphan-debounce-soft_delete
// cleanup / queued jobs / csr_ one-off scripts. Verified against the full action
// inventory — no user-facing verb (created/updated/deleted/archived/unarchived/
// generated/uploaded) contains any of these substrings.
const HIDDEN_ACTION_SUBSTRINGS = [
  "sync",
  "email",
  "auto_",
  "orphan",
  "debounce",
  "soft_delete",
  "queued",
  "csr_",
];

// Internal/engineering event namespaces whose dotted breadcrumb actions
// (coi_request.created/…, intercom_event.received/…) are hidden. Matched
// by explicit prefix, not a blanket "any dot" rule, so an unrelated dotted
// action added later isn't silently swallowed. The single exception within the
// coi_request. namespace is coi_request.sent, explicitly surfaced via
// SURFACED_COI_ACTIONS below (CSR-1878).
const HIDDEN_ACTION_NAMESPACES = ["coi_request.", "intercom_event."];

// CSR-1878 — the two "COI sent" events, explicitly surfaced as a single
// "COI sent" Docs card:
//   - coi_request.sent — inbound-request approval / auto-send path
//     (server/coi-request-worker.ts). It lives IN the hidden "coi_request."
//     namespace, so being in this set is what carves it back out.
//   - coi.sent — user-initiated "Send COI" path (server/coi-email-worker.ts);
//     a distinct action because that send has no coi_request behind it. There
//     is NO "coi." hidden namespace, so this action was never hidden — listing
//     it here is an explicit surface (and keeps any future hide rule from
//     accidentally catching it), not a carve-out.
// Both render through ONE path here: the surfacing early-return, the "Docs"
// entity label, and the curated detail branch are all gated on this set; only
// the title map lists both (mapping each to "COI sent"). Every OTHER
// coi_request.* / intercom_event.* breadcrumb stays hidden by the namespace rule.
export const SURFACED_COI_ACTIONS = new Set(["coi_request.sent", "coi.sent"]);

export function isHiddenActivityLog(log: ActivityLogLike): boolean {
  const action = (log.action ?? "").toLowerCase();
  if (SURFACED_COI_ACTIONS.has(action)) return false;
  if (HIDDEN_ACTIONS.has(action)) return true;
  if (HIDDEN_ACTION_NAMESPACES.some((ns) => action.startsWith(ns))) return true;
  if (HIDDEN_ACTION_SUBSTRINGS.some((s) => action.includes(s))) return true;
  // Spec — hide ONLY genuine no-op / identifier-only "updated" rows ("policy
  // updated with only a policy number and no change details"), while every real
  // mutation stays visible. Real user actions include the CSR-1845 diff shapes
  // (changes/changed/coverageKeys/assetStatusChange/note) AND flat-map upkeep
  // (clearedCoiTemplate / savedCoiTemplate / coiNotes) — see
  // hasMeaningfulChangeContent. Only applies to policy/insured; other entities
  // (agencies, certificate holders, …) are never suppressed here.
  if (
    action === "updated" &&
    (log.entityType === "policy" || log.entityType === "insured")
  ) {
    const details = parseActivityDetails(log.details);
    // Non-object details: truly empty (null / "") is a no-op → hide; a
    // non-JSON string still renders a line → keep visible.
    if (!details) return log.details == null || log.details.trim() === "";
    return !hasMeaningfulChangeContent(details);
  }
  return false;
}

// True when an "updated" policy/insured `details` carries real user-edited
// content. Mirrors buildActivityDetailLines' precedence but discounts pure
// identifiers (IDENTIFIER_ONLY_KEYS) and the internal agencyId FK, so a bare
// `{ policyNumber }` counts as no content (hidden) while a genuine flat-map
// mutation counts (shown).
function hasMeaningfulChangeContent(details: Record<string, unknown>): boolean {
  if (readChanges(details).length > 0) return true;
  const changed = Array.isArray(details.changed) ? details.changed : [];
  if (changed.some((f) => typeof f === "string" && f !== "agencyId")) {
    return true;
  }
  if (Array.isArray(details.coverageKeys) && details.coverageKeys.length > 0) {
    return true;
  }
  if (details.assetStatusChange) return true;
  if (typeof details.note === "string" && details.note.trim() !== "") {
    return true;
  }
  if (Object.keys(FLAT_ACTION_LINES).some((key) => key in details)) return true;
  // Any other non-identifier scalar key is a real flat-map mutation.
  for (const [key, value] of Object.entries(details)) {
    if (HANDLED_DETAIL_KEYS.has(key)) continue;
    if (IDENTIFIER_ONLY_KEYS.has(key)) continue;
    if (value === null || value === undefined) continue;
    if (isScalar(value)) return true;
  }
  return false;
}

// ---------------- parsing ----------------

export function parseActivityDetails(
  raw: string | null | undefined,
): Record<string, unknown> | null {
  if (raw == null || raw === "") return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function readChanges(details: Record<string, unknown>): ActivityFieldChange[] {
  if (!Array.isArray(details.changes)) return [];
  return details.changes.filter(
    (c): c is ActivityFieldChange =>
      c != null &&
      typeof c === "object" &&
      typeof (c as ActivityFieldChange).field === "string",
  );
}

// ---------------- formatting ----------------

export function activityFieldLabel(field: string): string {
  return (
    FIELD_LABELS[field] ??
    INSURED_FIELD_LABELS[field] ??
    EXTRA_KEY_LABELS[field] ??
    titleCaseKey(field)
  );
}

function titleCaseKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

export function formatActivityFieldValue(
  field: string,
  value: unknown,
): string {
  if (value === null || value === undefined || value === "") {
    return EMPTY_DISPLAY;
  }
  if (typeof value === "object") return EMPTY_DISPLAY;
  if (MONEY_ACTIVITY_FIELDS.has(field))
    return formatCurrencyValue(String(value));
  if (DATE_ACTIVITY_FIELDS.has(field)) {
    const formatted = formatLongDate(String(value));
    return formatted === "—" ? EMPTY_DISPLAY : formatted;
  }
  const str = String(value);
  if (field === "status") return str.charAt(0).toUpperCase() + str.slice(1);
  if (field === "substatus" && isPolicySubstatus(str)) return formatPolicySubstatusLabel(str);
  return str;
}

// CSR-2274 — the COI service's hourly sweep records carrier-driven Additional
// Insured status changes with no user; they read as the carrier sync.
export const CARRIER_SYNC_ACTOR = "Synced from carrier";

export function isCarrierSyncedStatusChange(log: ActivityLogLike): boolean {
  if (log.entityType !== "additional_insured" || log.userId) return false;
  return parseActivityDetails(log.details)?.source === "automation";
}

// CSR-2274 — the one "Status changed from … to …" line of an Additional
// Insured status change, or null when the row changed no status.
export function additionalInsuredStatusLine(log: ActivityLogLike): string | null {
  if (log.entityType !== "additional_insured") return null;
  const details = parseActivityDetails(log.details);
  const change = details ? readChanges(details).find((c) => c.field === "status") : undefined;
  if (!change) return null;
  return `${activityFieldLabel("status")} changed from ${formatActivityFieldValue("status", change.oldValue)} to ${formatActivityFieldValue("status", change.newValue)}`;
}

export function formatActivityActor(log: ActivityLogLike): string {
  if (isCarrierSyncedStatusChange(log)) return CARRIER_SYNC_ACTOR;
  // userId is the last-resort fallback before "AMS System" so a real user
  // action whose name/email join is missing is never misattributed as system
  // (preserves origin/main's userName || userEmail || userId chain).
  return log.userName ?? log.userEmail ?? log.userId ?? "AMS System";
}

// CSR-1878 — human titles for surfaced actions the page would otherwise render
// raw (snake_case). Both "COI sent" actions map to the same spec title so the
// two send paths present identically. Unmapped actions pass through unchanged.
const ACTION_TITLES: Record<string, string> = {
  "coi_request.sent": "COI sent",
  "coi.sent": "COI sent",
};

export function formatActivityAction(action: string): string {
  return ACTION_TITLES[action.toLowerCase()] ?? action;
}

// CSR-1878 — the Event Logging Spec places the "COI sent" event in the "Docs"
// category. The Activity Log page has no category column, so the surfaced send
// rows borrow the Entity Type cell to show "Docs"; every other row keeps its
// stored entity type.
export function activityEntityTypeLabel(log: ActivityLogLike): string {
  if (SURFACED_COI_ACTIONS.has((log.action ?? "").toLowerCase())) return "Docs";
  return log.entityType ?? "";
}

// ---------------- detail lines ----------------

function isScalar(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function singularAsset(arrayKey: unknown): string {
  if (arrayKey === "vehicles") return "vehicle";
  if (arrayKey === "trailers") return "trailer";
  if (arrayKey === "drivers") return "driver";
  return "asset";
}

function trimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

// CSR-1878 — curated detail lines for a surfaced "COI sent" row. Emits only the
// human-facing scalars (holder, recipient, insured, policy number) in spec
// order, skipping any that are absent, so the many internal keys that share the
// details blob (issuanceId, emailSent, syncStatus, redirected, dc-ids, …) never
// leak as "Email Sent: true" / "Policy Id: dc-…" noise. Historical rows written
// before the CSR-1878 server enrichment lack insuredName/policyNumber and
// degrade gracefully to just the "Sent to …" line.
export function buildCoiSentDetailLines(details: Record<string, unknown>): string[] {
  const lines: string[] = [];

  // CSR-1746 — a literal "N/A" holder is treated as empty so the line degrades
  // to "Sent to <recipient>" (or drops) instead of rendering "Sent to N/A".
  // Shared base builder → the insured timeline, the Policy tab, and the global
  // log all inherit this.
  const rawHolder = trimmedString(details.certificateHolderName);
  const holder = rawHolder.toLowerCase() === "n/a" ? "" : rawHolder;
  const recipient =
    trimmedString(details.recipient) || trimmedString(details.actualRecipient);
  if (holder && recipient) lines.push(`Sent to ${holder} (${recipient})`);
  else if (holder) lines.push(`Sent to ${holder}`);
  else if (recipient) lines.push(`Sent to ${recipient}`);

  const insured = trimmedString(details.insuredName);
  if (insured) lines.push(`Insured: ${insured}`);

  const policyNumber = trimmedString(details.policyNumber);
  if (policyNumber) lines.push(`Policy ${policyNumber}`);

  return lines;
}

// Builds the bullet lines for one activity row, in precedence order:
//   1. `changes` (before→after, spec formatting)
//   2. fields in `changed` not covered by `changes` (JSONB/legacy) → "<Label> updated"
//   3. legacy `coverageKeys` → "Coverages updated"
//   4. `assetStatusChange` → "Added/Removed <asset>"
//   5. `note` → "Note: <note>"
//   6. known flat maintenance shapes (COI template / notes) → a fixed line
//   7. flat `{ key: value }` maps (create rows) → "<Label>: <value>", scalars only
//   8. unparseable / plain-string details → the raw string
// Returns [] when there is nothing to show (the page renders "—").
export function buildActivityDetailLines(log: ActivityLogLike): string[] {
  if (log.details == null || log.details.trim() === "") return [];
  const details = parseActivityDetails(log.details);
  if (!details) return [log.details];

  // CSR-1878 — surfaced "COI sent" rows render a curated line set; this MUST
  // run before the generic branches below, or the internal scalar keys in the
  // send row's details would flat-map into noise.
  if (SURFACED_COI_ACTIONS.has((log.action ?? "").toLowerCase())) {
    return buildCoiSentDetailLines(details);
  }

  const lines: string[] = [];

  const changes = readChanges(details);
  for (const change of changes) {
    const before = formatActivityFieldValue(change.field, change.oldValue);
    const after = formatActivityFieldValue(change.field, change.newValue);
    // CSR-1746 — drop a formatted no-op line (before === after, e.g. an
    // ISO/format-only date delta) so it never renders "X changed from V to V".
    // The row-hiding predicate (isHiddenActivityLog) is intentionally left
    // untouched — it keys on raw presence, so a row whose only change is a
    // formatted no-op now renders "—" rather than a phantom line.
    if (before === after) continue;
    lines.push(
      `${activityFieldLabel(change.field)} changed from ${before} to ${after}`,
    );
  }

  const coveredFields = new Set(changes.map((c) => c.field));
  const changed = Array.isArray(details.changed) ? details.changed : [];
  for (const field of changed) {
    if (typeof field !== "string" || coveredFields.has(field)) continue;
    // agencyId is an internal FK the server already excludes from `changes`
    // (echo drift, never a user edit) — don't render it from `changed` either.
    if (field === "agencyId") continue;
    coveredFields.add(field);
    lines.push(`${activityFieldLabel(field)} updated`);
  }

  if (
    Array.isArray(details.coverageKeys) &&
    details.coverageKeys.length > 0 &&
    !coveredFields.has("coverages")
  ) {
    lines.push("Coverages updated");
  }

  const assetChange = details.assetStatusChange;
  if (assetChange && typeof assetChange === "object") {
    const { arrayKey, removed, failed } = assetChange as Record<
      string,
      unknown
    >;
    const verb = removed ? "Removed" : "Added";
    lines.push(
      `${verb} ${singularAsset(arrayKey)}${failed ? " (not applied)" : ""}`,
    );
  }

  if (typeof details.note === "string" && details.note.trim() !== "") {
    lines.push(`Note: ${details.note}`);
  }

  if (lines.length > 0) return lines;

  // Known flat maintenance shapes (COI template / notes) → a fixed human line,
  // so clearedCoiTemplate/savedCoiTemplate read cleanly and the array-valued
  // coiNotes renders content instead of an empty cell.
  for (const [key, line] of Object.entries(FLAT_ACTION_LINES)) {
    if (key in details) lines.push(line);
  }
  if (lines.length > 0) return lines;

  // Flat create-style maps: scalar values only, so raw JSON or
  // "[object Object]" can never render.
  for (const [key, value] of Object.entries(details)) {
    if (HANDLED_DETAIL_KEYS.has(key)) continue;
    if (!isScalar(value)) continue;
    lines.push(`${activityFieldLabel(key)}: ${String(value)}`);
  }
  return lines;
}
